import React from "react";
import { FiMapPin } from "react-icons/fi";
import Logo from "../styles/Black-SVG-Landscape.svg";
import JuanFelipePhoto from "../candidates/juan.png";

export default function ContentProducerRoleJuanFelipe() {
  const candidateProfileCopy = {
    name: "Juan Felipe",
    location: "Bogotá, Colombia",
    compensation: {
      range: "$13 / hourly",
    },
    availability: "Available to start immediately",

    working_style:
      "Juan Felipe is a technically elite post-production professional who brings cinematic production standards to every deliverable. He works with strong attention to craft: color grading, compositing, motion graphics, and editorial rhythm are not afterthoughts for him — they are the baseline. He prefers to own the editing process manually, applying his own creative judgment on pacing, cuts, and visual storytelling rather than relying on auto-edit platforms. He has operated within structured, deadline-driven agency pipelines across international brands and led small post-production teams, making him reliable and accountable in client-facing environments.",

    loom_video_link: "https://fathom.video/share/sh2SHCmbcxs-2rQ_sB9hyakZcrks-dKv",

    tools: [
      {
        category: "Post-Production & Editing",
        items: [
          "Adobe Premiere Pro",
          "Final Cut Pro",
          "DaVinci Resolve (color grading — expert level)",
          "After Effects (motion graphics)",
          "Adobe Audition (audio finishing)",
          "Photoshop (retouching + compositing)",
          "Illustrator (graphic assets)",
        ],
      },
      {
        category: "Short-Form & Social Content",
        items: [
          "Reels, Shorts, and Stories editing",
          "Hook crafting (first 3–5 seconds: visual + motion graphics)",
          "Pacing and rhythm for short-form formats",
          "Bulk short-form content production for social media",
          "Motion graphics overlays for scroll-stopping openers",
        ],
      },
      {
        category: "Broadcast & Commercial Production",
        items: [
          "TV commercial post-production",
          "National and international broadcast delivery",
          "Documentary color grading and finalization",
          "Feature film post-production support",
          "Corporate and institutional video production",
        ],
      },
      {
        category: "Photography",
        items: [
          "Still photography for events and productions",
          "Photo editing and retouching (Photoshop)",
          "Promotional photography for social media and festivals",
        ],
      },
      {
        category: "AI Tools",
        items: [
          "AI tools for research and topic discovery",
          "AI-assisted keyword and content gap identification",
        ],
      },
    ],

    soft_skills: [
      "Cinematic storytelling instincts from film production background",
      "High attention to craft: color, pacing, rhythm, and finish",
      "Selective and brand-aware about trend adoption",
      "Experienced in structured agency post-production pipelines",
      "Led post-production teams (4 people)",
      "Deadline-driven and quality-accountable in client-facing environments",
      "Comfortable with multicultural, international brand standards",
    ],

    about_candidate:
      "Juan Felipe is a professional filmmaker and post-production specialist with 5+ years of agency experience at top-tier production houses. He holds a Master's in Digital Post-Production and brings expert-level skills in DaVinci Resolve color grading, After Effects motion graphics, and cinematic editorial finishing. He has shipped commercial content for international brands across national TV, broadcast, and digital platforms. His production quality ceiling is the highest in this candidate group.",

    core_strengths: [
      {
        headline: "Cinematic short-form video production",
        description:
          "Applies the same editorial discipline — pacing, rhythm, purposeful cuts, color grading — to short-form Reels and Shorts that he brings to broadcast commercials. Every deliverable feels intentional and professionally finished, not auto-edited.",
      },
      {
        headline: "Motion graphics as a scroll-stopping hook layer",
        description:
          "Uses After Effects motion graphics within the first 3–5 seconds of short-form content — quick, bright, visually commanding moments — as an attention layer that most social-native editors cannot produce.",
      },
      {
        headline: "Expert color grading and visual polish",
        description:
          "DaVinci Resolve specialist with agency-level color finishing experience across commercials, documentaries, and digital content. Elevates raw footage into visually premium assets that signal professionalism and quality to high-value audiences.",
      },
      {
        headline: "Construction-adjacent content production",
        description:
          "Has produced content for a construction company through agency work — a direct data point showing familiarity with the industry context that most production-focused candidates in this pool lack.",
      },
    ],

    relevant_experience: [
      {
        headline: "Content Editor | Power Digital | Nov 2024–Present",
        bullets: [
          "Video editing for digital content and commercials for international brands",
          "Motion graphics, color grading, and editing of organic content for social media, television, and websites",
        ],
      },
      {
        headline: "Editor and Colorist | PXP / Publicis Groupe | Apr–Oct 2024",
        bullets: [
          "Post-production of commercials and digital pieces specializing in color grading and motion graphics",
          "Maintained high quality standards across international brand campaigns",
        ],
      },
      {
        headline: "Color Grading | Fujifilm | May–Jun 2024",
        bullets: [
          "Color grading for a promotional documentary piece",
        ],
      },
      {
        headline: "Post Production Leader | Havas Media Group | Feb 2023–Dec 2023",
        bullets: [
          "Led post-production team of 4 across social media, national and international TV, and websites",
          "Responsible for editing, color grading, and motion graphics across multiple brand accounts",
        ],
      },
      {
        headline: "Motion Graphics Specialist | Havas Media Group | May 2022–Feb 2023",
        bullets: [
          "Editing, color grading, and motion graphics for social media, national and international television, and websites",
        ],
      },
      {
        headline: "Post Production / Filmmaking | Simón Sein Agency | Mar–May 2022",
        bullets: [
          "Conceptualization, production, and post-production for brand clients",
          "Directed editing, colorization, and motion graphics for social networks and web pages",
        ],
      },
      {
        headline: "Filmmaking | Happy Media Hub | Nov–Dec 2021",
        bullets: [
          "Produced content for agency clients including a construction company",
        ],
      },
    ],
  };

  // ─── PAGE 2 DATA ─────────────────────────────────────────────────────────────
  const technicalAssessment = {
    meta: {
      role: "Content Producer — Handy Manny's Remodeling",
      interviewDate: "March 06, 2026",
      analyst: "AI-Architechs Senior Technical Hiring Analysis",
    },

    snapshot:
      "Juan is an exceptionally skilled post-production professional and filmmaker whose technical video craft sits at the top of this candidate pool — his background includes agency work for multinational brands, motion graphics, color grading at DaVinci Resolve level, and a Master's in Digital Post-Production. However, his profile is primarily that of a technical executor and post-production specialist rather than a content strategist or marketing-driven content producer. His interview answers demonstrate conceptual awareness of hooks, narrative, and CTAs, but lack the specificity, tactical depth, and lead-gen fluency that Lihué and Daniel bring from active marketing and content strategy roles. He is a strong choice if premium video production quality is the immediate top priority; he is a higher risk if you need the Content Producer to also drive strategy, copywriting, topic research, and content system design independently.",

    scorecard: [
      {
        dimension: "AI & Content Tools",
        score: 5,
        rationale:
          "Uses AI primarily for research and knowledge-gathering about unfamiliar topics (\"to find the spots, the words, the bullets\"). Does not describe a systematic AI content workflow — no mention of specific AI video generation platforms, image generation tools, AI audio, or prompt engineering practices. References are general (\"AI is a huge improvement\") rather than tool-specific. Significant gap compared to peers in this pool.",
      },
      {
        dimension: "Core Platforms & Publishing Ops",
        score: 5,
        rationale:
          "CV lists Premiere, Final Cut Pro, After Effects, DaVinci Resolve, Illustrator, and Photoshop — all post-production tools. No mention of social media scheduling platforms, content calendars, CMS publishing, Meta Business Suite, or multi-platform distribution operations. His experience is in agency post-production pipelines, not in managing the end-to-end content publishing workflow a Content Producer role requires.",
      },
      {
        dimension: "Performance & Lead Gen Mindset",
        score: 5,
        rationale:
          "Understands the basic framework of CTAs, narrative that identifies pain points, and directing audiences toward action. However, examples in the interview are thin and generalized — references lifestyle brands, surveys, and website links without concrete results or specifics. Does not mention metrics, performance diagnosis beyond \"reshape the messaging,\" or connecting content to actual lead outcomes. Conceptually aware but not yet operationally practiced in lead-gen content.",
      },
      {
        dimension: "Short-Form Video Production & Editing",
        score: 10,
        rationale:
          "This is Juan's undisputed, best-evidenced strength. Master's in Digital Post-Production. 5+ years of professional agency experience including Havas Media Group (Post-Production Leader managing a team of 4), PXP/Publicis Groupe, Power Digital (currently), Fujifilm, and multiple international brands. Expert in DaVinci Resolve color grading, After Effects motion graphics, Premiere, Final Cut Pro, and compositing. Has shipped commercial content for Nissan, Colgate, AstraZeneca, Scrum Alliance, Oreo, and more. This level of production craft is rare in this candidate pool.",
      },
      {
        dimension: "Storytelling, Hooks & Audience Psychology",
        score: 7,
        rationale:
          "Articulates a clear three-part content structure: hook → body with defined style → CTA aligned to purpose. Describes narrative that speaks to pain points and positions the brand as the solution. Brings up the McDonald's vs. Burger King example to illustrate competitive positioning and brand authenticity — showing he thinks in strategic creative terms, not just technical execution. Loses points for not going deeper on emotional triggers, homeowner-specific psychology, or direct-response mechanics.",
      },
      {
        dimension: "Search Intent & Topic Research",
        score: 4,
        rationale:
          "References monitoring competitor comments to find audience pain points and complaints — a valid but surface-level approach. Does not mention keyword tools, SEO-aware content writing, SERP research, or platform autocomplete/trending topic analysis. This is a genuine gap for a role where search visibility and AI discoverability are core outcomes.",
      },
      {
        dimension: "Systemic Thinking & Content Engine Design",
        score: 5,
        rationale:
          "No evidence of building or operating a content system — calendars, input-to-output pipelines, or repeatable production workflows at the content strategy level. His agency experience involves executing well within defined production pipelines built by others, not designing the content engine from scratch. Led a team of 4 in post-production, which shows operational leadership, but scoped to the editing/finishing phase only.",
      },
      {
        dimension: "Communication, Ownership & Collaboration",
        score: 6,
        rationale:
          "English is functional and understandable, but noticeably less fluent than the other candidates — some pauses, rephrasing, and occasional imprecision in word choice during the interview. Communicates ideas with sincerity and clarity at a conceptual level. Would be adequate for day-to-day async collaboration but may create friction in fast-paced strategic discussions or written copy for English-language audiences.",
      },
      {
        dimension: "Time Zone & Overlap Fit",
        score: 8,
        rationale:
          "Based in Bogotá, Colombia (currently) — UTC-5 year-round, same or near-identical to U.S. Eastern. Full working-day overlap with Manny's 8am–8pm ET window is seamless. Note: CV indicates a Master's completed in Valencia, Spain (Jul 2025) — current location should be confirmed. If still Colombia-based, overlap is excellent.",
      },
    ],

    strengths: [
      {
        headline: "Highest production quality ceiling in the candidate pool — by a significant margin",
        description:
          "Juan's post-production credentials (DaVinci Resolve color grading, After Effects motion graphics, agency experience at Havas, PXP/Publicis, Power Digital) mean he can produce commercial-grade video content — not just social-native clips. For Manny's $50K–$1M remodeling projects, where the visual quality of showcase content signals the caliber of the business to developers and property owners, this is a meaningful differentiator. His reel shows work produced for Fujifilm, international FMCG brands, and national TV.",
      },
      {
        headline: "Motion graphics and visual storytelling as a differentiator",
        description:
          "Juan unprompted describes adding motion graphics elements to short-form content as a way to stop the scroll — \"something that will appear over the screen and literally captivate attention as a flame.\" This capability, combined with his DaVinci Resolve and After Effects expertise, means he can elevate raw jobsite footage into visually polished, motion-enhanced content that most social-only creators cannot match.",
      },
      {
        headline: "Agency post-production leadership experience",
        description:
          "Led a team of 4 at Havas Media Group handling post-production for Colmédica, Allianz, Enfagrow, and Procolombia. This shows he can operate in a structured, client-accountable, deadline-driven production environment — which is directly relevant to the consistent content cadence Manny's engine requires.",
      },
      {
        headline: "Has prior construction-adjacent content experience",
        description:
          "CV notes filmmaking work for KMA Construcciones through Happy Media Hub — a rare direct data point showing he has produced content for a construction-related company. This is a small but meaningful signal that he can adapt his production style to the remodeling/construction context faster than a candidate with zero industry touchpoints.",
      },
    ],

    flags: [
      {
        headline: "Lead generation strategy and marketing operations are under-developed",
        description:
          "Juan's interview answers on lead-gen are conceptually correct but operationally thin. He cannot describe a real campaign where his content drove specific leads, inquiries, or conversions. He does not reference funnels, CTAs connected to tracking, ManyChat, email sequences, UTM links, or any of the mechanics that make content actually generate inbound for a local service business. This is a meaningful gap for the role as scoped — Manny needs content that functions as a lead engine, not just beautiful production. This is a hard constraint if he operates solo.",
      },
      {
        headline: "AI toolchain is not developed for content production workflows",
        description:
          "Uses AI for research only — no evidence of AI image generation, AI video generation, AI voiceover, prompt engineering for content creation, or AI-assisted scriptwriting. In a role that explicitly values AI tools in the workflow to move faster, this is a notable gap relative to both Daniel and Lihué. Manageable if paired with a strong AI Architect who provides the AI-generated asset layer, but it limits his independence.",
      },
      {
        headline: "Publishing and content operations experience is absent",
        description:
          "No experience with content calendars, scheduling tools, social media management platforms, or multi-platform publishing workflows. His professional world has been agency post-production pipelines — receiving briefs and delivering finished assets — not owning the content engine end-to-end. This means significant ramp time on the operational side of the Content Producer role.",
      },
      {
        headline: "English fluency may create friction in copywriting and strategic communication",
        description:
          "His English is functional for collaboration but not at the level of fluency needed for writing hooks, captions, and scripts for a U.S. English-speaking homeowner audience. Direct response copywriting in English — one of the core deliverables in this role — is an area where his output would likely need consistent review and editing. Manageable if there is a review layer in place, but worth flagging explicitly.",
      },
      {
        headline: "Search intent and SEO-aware content writing is undeveloped",
        description:
          "No evidence of keyword research, SEO-aware copywriting, or topic research practices. For Manny's content engine, where blog-driven search visibility and AI discoverability are explicit goals, this gap creates dependency on the AI Architect to supply all strategic direction on what topics to produce.",
      },
    ],

    when_to_hire: [
      "Premium video production quality is the single most important immediate priority — and you are willing to pair him with a strong strategist (Lihué or the AI Architect) who owns the content direction, topics, copywriting, and lead-gen mechanics.",
      "You are building toward high-production-value showcase content for $100K–$1M commercial build-out clients where the visual standard needs to signal a professional, premium contractor.",
      "The AI Architect is already in place and can supply AI-generated assets, topic briefs, scripts, and content system design — freeing Juan to focus on what he does best: editing, motion graphics, color, and visual storytelling.",
      "You want someone who has operated inside structured, deadline-driven, client-accountable agency pipelines and can produce consistently at a high technical bar.",
    ],

    when_to_be_cautious: [
      "You need the Content Producer to independently own strategy, copywriting, lead funnels, topic research, and content system design alongside production — Juan is not equipped for the full scope today.",
      "Direct response copywriting and hook writing in English for U.S. homeowner audiences are core daily outputs — his English fluency level creates a meaningful bottleneck here.",
      "You need AI-fluent content workflows and expect the producer to leverage AI generation tools to increase content volume and speed without additional technical support.",
      "The AI Architect role is not yet in place and Juan would be building and running the full content engine alone.",
    ],

    bottom_line:
      "Juan is a technically elite post-production professional whose video craft is genuinely best-in-class among the three candidates. The gap is everything outside the edit suite — strategy, copywriting, lead-gen mechanics, AI workflows, and publishing operations. He is the right hire if you are building a two-person content team where a strategist-operator (like Lihué) owns the marketing layer and Juan owns the production layer. As a standalone Content Producer for the full role as scoped today, he is not the strongest fit — but as part of a content team, his ceiling is the highest.",
  };

  // Score pill helper
  const ScorePill = ({ score }) => {
    let bg = "#f97316";
    if (score >= 8) bg = "#22c55e";
    else if (score >= 6) bg = "#36D1FF";
    return (
      <span
        style={{ backgroundColor: bg }}
        className="inline-block text-white text-xs font-bold px-3 py-1 rounded-full"
      >
        {score} / 10
      </span>
    );
  };

  const weightedAvg = (
    technicalAssessment.scorecard.reduce((sum, r) => sum + r.score, 0) /
    technicalAssessment.scorecard.length
  ).toFixed(1);

  return (
    <div className="min-h-screen bg-[#01014C] text-white flex flex-col items-center py-10 px-4">

      {/* ===================== PAGE 1 ===================== */}
      <div className="w-full max-w-6xl bg-[#FFFDFB] rounded-3xl overflow-hidden shadow-2xl flex flex-col md:flex-row relative">

        {/* LEFT COLUMN */}
        <aside className="relative md:w-1/3 bg-gradient-to-b from-[#01014C] via-[#0A0A80] to-[#36D1FF] p-8 flex flex-col items-center text-center overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-b from-[#36D1FF]/30 to-transparent"></div>

          {/* Avatar */}
          <div className="relative mb-4">
            <div className="absolute inset-0 rounded-full bg-[#36D1FF]/20 blur-2xl"></div>
            <img
              src={JuanFelipePhoto}
              alt={candidateProfileCopy.name}
              className="relative w-36 h-36 rounded-full object-cover border-4 border-[#36D1FF] shadow-lg"
            />
          </div>

          {/* Name */}
          <h1 className="text-2xl font-semibold mb-1 text-[#FFFDFB]">
            {candidateProfileCopy.name}
          </h1>

          {/* Location */}
          <div className="flex items-center gap-2 text-[#E0F7FF] text-sm mb-6 opacity-90">
            <FiMapPin className="text-[#36D1FF]" />
            <span>{candidateProfileCopy.location}</span>
          </div>

          {/* Compensation */}
          <div className="bg-[#01014C] border border-[#36D1FF]/50 rounded-xl p-4 w-full text-left mb-4">
            <p className="text-[#B3E9FF] text-sm font-semibold uppercase mb-1">
              Compensation
            </p>
            <p className="text-[#36D1FF] text-lg font-bold">
              {candidateProfileCopy.compensation.range}
            </p>
          </div>

          {/* Availability */}
          <div className="bg-[#01014C] border border-[#36D1FF]/30 rounded-xl p-4 w-full text-left mb-6">
            <p className="text-[#E0F7FF] text-sm">
              {candidateProfileCopy.availability}
            </p>
          </div>

          {/* Video Intro */}
          {candidateProfileCopy.loom_video_link && (
            <div className="w-full mt-4">
              <a
                href={candidateProfileCopy.loom_video_link}
                target="_blank"
                rel="noreferrer"
                className="block w-full text-center font-semibold py-3 rounded-lg
                           bg-[#36D1FF] text-[#01014C]
                           hover:bg-[#5FE2FF]
                           transition-all duration-300"
              >
                🎥 Watch Interview
              </a>
            </div>
          )}

          {/* Working Style */}
          <div className="bg-[#36D1FF]/5 border border-[#36D1FF]/20 p-4 rounded-xl text-sm text-[#E0F7FF] mb-6 mt-6">
            {candidateProfileCopy.working_style}
          </div>

          {/* Tools */}
          <div className="w-full mb-6 text-left">
            <h3 className="text-sm font-semibold mb-2 text-[#B3E9FF] uppercase">
              Tools
            </h3>
            {candidateProfileCopy.tools.map((group, i) => (
              <div key={i} className="mb-3">
                <p className="text-xs text-[#E0F7FF]/80 mb-1">{group.category}</p>
                <div className="flex flex-wrap gap-2">
                  {group.items.map((tool, j) => (
                    <span
                      key={j}
                      className="bg-[#36D1FF]/10 text-[#36D1FF] text-xs px-2 py-1 rounded-full border border-[#36D1FF]/30"
                    >
                      {tool}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Soft Skills */}
          <div className="w-full mb-6 text-left">
            <h3 className="text-sm font-semibold mb-2 text-[#B3E9FF] uppercase">
              Soft Skills
            </h3>
            <ul className="list-disc pl-5 space-y-1 text-[#E0F7FF] text-sm">
              {candidateProfileCopy.soft_skills.map((skill, i) => (
                <li key={i}>{skill}</li>
              ))}
            </ul>
          </div>

          {/* Logo */}
          <div className="w-full mt-8 flex justify-center">
            <img src={Logo} alt="AI-Architechs Logo" className="w-42 opacity-80" />
          </div>
        </aside>

        {/* RIGHT COLUMN */}
        <main className="flex-1 p-10 text-[#01014C] space-y-10">
          <section>
            <h2 className="text-xl font-semibold mb-3 border-b border-[#36D1FF]/40 pb-1">
              About this Candidate
            </h2>
            <p className="text-gray-700 leading-relaxed whitespace-pre-line">
              {candidateProfileCopy.about_candidate}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3 border-b border-[#36D1FF]/40 pb-1">
              Core Strengths
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {candidateProfileCopy.core_strengths.map((item, i) => (
                <div key={i} className="border border-[#36D1FF]/30 rounded-xl p-5">
                  <p className="font-semibold mb-1">{item.headline}</p>
                  <p className="text-sm text-gray-700">{item.description}</p>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3 border-b border-[#36D1FF]/40 pb-1">
              Relevant Experience
            </h2>
            <div className="space-y-6">
              {candidateProfileCopy.relevant_experience.map((exp, i) => (
                <div key={i} className="border border-[#36D1FF]/30 rounded-xl p-5">
                  <p className="font-semibold text-[#EB5D2A] mb-2">{exp.headline}</p>
                  <ul className="list-disc pl-6 space-y-1 text-sm text-gray-700">
                    {exp.bullets.map((b, j) => (
                      <li key={j}>{b}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>
        </main>
      </div>

      {/* ===================== PAGE BREAK BAND ===================== */}
      <div className="w-full py-10">
        <div className="max-w-6xl mx-auto px-4">
          <div className="h-10 rounded-2xl bg-gradient-to-r from-[#01014C] via-[#0A0A80] to-[#36D1FF] opacity-70 shadow-[0_0_40px_#36D1FF20]" />
        </div>
      </div>

      {/* ===================== PAGE 2 ===================== */}
      <div className="w-full max-w-6xl bg-[#FFFDFB] rounded-3xl overflow-hidden shadow-2xl pb-2">
        <main className="p-10 text-[#01014C]">
          <div className="border border-[#36D1FF]/25 rounded-2xl p-6 bg-white shadow-[0_0_12px_#36D1FF15]">

            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-2 mb-6">
              <h2 className="text-2xl font-semibold text-[#01014C]">
                Technical Analysis
              </h2>
              <div className="flex flex-col items-start md:items-end gap-1 text-xs text-gray-400">
                <span>{technicalAssessment.meta.role}</span>
                <span>Interview: {technicalAssessment.meta.interviewDate}</span>
                <span>{technicalAssessment.meta.analyst}</span>
              </div>
            </div>

            {/* Snapshot */}
            <div className="border border-[#36D1FF]/20 rounded-xl p-5 bg-[#36D1FF]/5 mb-6">
              <p className="text-base font-bold text-[#01014C] mb-2">Executive Fit Snapshot</p>
              <p className="text-gray-700 text-base leading-relaxed whitespace-pre-line">
                {technicalAssessment.snapshot}
              </p>
            </div>

            {/* Strengths & Flags */}
            <div className="grid md:grid-cols-2 gap-6 mb-6">

              {/* Strengths */}
              <div>
                <h3 className="text-sm font-semibold text-[#01014C] mb-3 uppercase tracking-wide">
                  ✅ Strengths
                </h3>
                <div className="space-y-3">
                  {technicalAssessment.strengths.map((item, i) => (
                    <div
                      key={i}
                      className="border border-[#22c55e]/30 rounded-xl p-4 bg-[#22c55e]/5"
                    >
                      <p className="font-semibold text-sm text-[#01014C] mb-1">
                        {item.headline}
                      </p>
                      <p className="text-sm text-gray-700">{item.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Flags */}
              <div>
                <h3 className="text-sm font-semibold text-[#01014C] mb-3 uppercase tracking-wide">
                  ⚠️ Flags / Watch-outs
                </h3>
                <div className="space-y-3">
                  {technicalAssessment.flags.map((item, i) => (
                    <div
                      key={i}
                      className="border border-[#f97316]/30 rounded-xl p-4 bg-[#f97316]/5"
                    >
                      <p className="font-semibold text-sm text-[#01014C] mb-1">
                        {item.headline}
                      </p>
                      <p className="text-sm text-gray-700">{item.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Scorecard */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-[#01014C] mb-3 uppercase tracking-wide">
                Quantitative Scorecard
              </h3>
              <div className="overflow-x-auto rounded-xl border border-[#36D1FF]/25">
                <table className="w-full text-sm">
                  <thead className="bg-[#36D1FF]/10 text-[#01014C]">
                    <tr>
                      <th className="text-left font-semibold px-4 py-3">Dimension</th>
                      <th className="text-left font-semibold px-4 py-3 w-[120px]">Score</th>
                      <th className="text-left font-semibold px-4 py-3">Rationale</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#36D1FF]/15">
                    {technicalAssessment.scorecard.map((row, idx) => (
                      <tr key={idx} className="bg-white">
                        <td className="px-4 py-3 font-medium text-[#01014C] align-top">
                          {row.dimension}
                        </td>
                        <td className="px-4 py-3 align-top">
                          <ScorePill score={row.score} />
                        </td>
                        <td className="px-4 py-3 text-gray-700 whitespace-pre-line align-top">
                          {row.rationale}
                        </td>
                      </tr>
                    ))}
                    {/* Weighted Average Row */}
                    <tr className="bg-[#01014C]/5 font-semibold">
                      <td className="px-4 py-3 text-[#01014C]">Weighted Average</td>
                      <td className="px-4 py-3">
                        <ScorePill score={parseFloat(weightedAvg)} />
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-sm italic">
                        Average across all 9 dimensions
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* When to Hire / Considerations */}
            <div className="grid md:grid-cols-2 gap-6 mb-6">

              {/* When to Hire */}
              <div>
                <h3 className="text-sm font-semibold text-[#01014C] mb-3 uppercase tracking-wide">
                  ✅ Hire Juan Felipe if…
                </h3>
                <div className="space-y-3">
                  {technicalAssessment.when_to_hire.map((item, i) => (
                    <div
                      key={i}
                      className="border border-[#36D1FF]/30 rounded-xl p-4 bg-[#36D1FF]/5 text-sm text-gray-700"
                    >
                      {item}
                    </div>
                  ))}
                </div>
              </div>

              {/* When to Be Cautious */}
              <div>
                <h3 className="text-sm font-semibold text-[#01014C] mb-3 uppercase tracking-wide">
                  ⚠️ Be cautious or pass if…
                </h3>
                <div className="space-y-3">
                  {technicalAssessment.when_to_be_cautious.map((item, i) => (
                    <div
                      key={i}
                      className="border border-[#f59e0b]/30 rounded-xl p-4 bg-[#f59e0b]/5 text-sm text-gray-700"
                    >
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Bottom Line */}
            <div className="border border-[#01014C]/20 rounded-xl p-5 bg-[#01014C]/5">
              <p className="text-sm font-bold text-[#01014C] mb-2 uppercase tracking-wide">
                🏁 Bottom Line
              </p>
              <p className="text-gray-700 text-sm leading-relaxed">
                {technicalAssessment.bottom_line}
              </p>
            </div>

          </div>
        </main>
      </div>

    </div>
  );
}
